/**
 * Copyright 2026, IntraLinks, Inc. All rights reserved.
 */

import React, { FC, useRef, useState } from 'react';
import { IlModalV2, IlLoading } from 'il-framework-component';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { I18nNamespaces } from '../../../app/shared/utils/constants.util';
import { ISecurityFactor } from '../../../domain/models/ISecuritySetup';
import { profileDeactivateFactor, DeactivateResult } from './profileEnrollSlice';
import { capitalizeFirstLetter } from '../../../app/shared/utils/helpers.util';
import { SecuritySetupType } from '../../../domain/models/impl/SecuritySetup';

interface DeactivateFactorModalProps {
  factor: ISecurityFactor | null;
  isOpen: boolean;
  onClose: () => void;
  onDeactivated: () => void;
}

const DeactivateFactorModal: FC<DeactivateFactorModalProps> = ({
  factor,
  isOpen,
  onClose,
  onDeactivated
}) => {
  const { t } = useTranslation(I18nNamespaces.LOA_ENRICHMENT);
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);

  // Retain the last factor so the modal content stays rendered during the close animation.
  const lastFactorRef = useRef<ISecurityFactor | null>(factor);
  if (factor) lastFactorRef.current = factor;
  const activeFactor = factor ?? lastFactorRef.current;

  const factorName = activeFactor?.label || activeFactor?.type || '';

  const onTranslation = (translationKey: string) => (
    <Trans
      i18nKey={translationKey}
      components={{ strong: <strong /> }}
    />
  );

  const handleDeactivate = async (): Promise<void> => {
    if (!activeFactor) return;
    setIsLoading(true);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const result: DeactivateResult = await dispatch(profileDeactivateFactor(t, activeFactor.id) as any);
    setIsLoading(false);
    if (result.status === 'success') {
      onDeactivated();
    }
    onClose();
  };

  const deactivateFactorTitle = (type: string) => {
    switch (type) {
      case SecuritySetupType.AUTHENTICATOR:
        return t('loaEnrichment.profile.deactivateModal.authenticatorTitle');
      case SecuritySetupType.SMS:
        return t('loaEnrichment.profile.deactivateModal.smsTitle');
      case SecuritySetupType.EMAIL:
        return t('loaEnrichment.profile.deactivateModal.emailTitle');
      default:
        return t('loaEnrichment.profile.deactivateModal.defaultTitle');
    };
  };

  const deactivateFactorBody = (type: string) => {
    switch (type) {
      case SecuritySetupType.AUTHENTICATOR:
        return onTranslation(t(
          'loaEnrichment.profile.deactivateModal.authenticatorBody',
          { factorName: capitalizeFirstLetter(factorName) }
        ));
      case SecuritySetupType.SMS:
        return onTranslation(t(
          'loaEnrichment.profile.deactivateModal.smsBody',
          { factorName: capitalizeFirstLetter(factorName) }
        ));
      case SecuritySetupType.EMAIL:
        return onTranslation(t(
          'loaEnrichment.profile.deactivateModal.emailBody',
          { factorName: capitalizeFirstLetter(factorName) }
        ));
      default:
        return onTranslation(t(
          'loaEnrichment.profile.deactivateModal.defaultBody',
          { factorName: capitalizeFirstLetter(factorName) }
        ));
    }
  };

  const modalBody = (
    <>
      {isLoading && <IlLoading active withOverlay />}
      <div id='deactivate-factor-body'>
        <p>{activeFactor ? deactivateFactorBody(activeFactor.type) : null}</p>
      </div>
    </>
  );

  return (
    <>
      <IlModalV2
        dialogBody={modalBody}
        size='sm'
        modalHeading={activeFactor ? deactivateFactorTitle(activeFactor.type) : ''}
        danger
        passiveModal
        open={isOpen}
        onRequestClose={onClose}
        preventCloseOnClickOutside
        selectorPrimaryFocus='#deactivate-factor-body'
        primaryButtonText={t('loaEnrichment.common.buttons.deactivate')}
        primaryButtonId='deactivateFactorBtn'
        secondaryButtonText={t('loaEnrichment.common.buttons.cancel')}
        secondaryButtonId='cancelDeactivateFactorBtn'
        tertiaryButtonText=''
        primaryButtonClick={handleDeactivate}
        primaryButtonDisabled={isLoading}
        secondaryButtonClick={onClose}
      />
    </>
  );
};

export default DeactivateFactorModal;
